package com.example.mystybag.common.ui.components.custom

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDefaults
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SelectableDates
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.ui.theme.LocalTheme.p54
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
actual fun DatePicker(
    modifier: Modifier,
    value: LocalDate?,
    onValueChange: (LocalDate?) -> Unit,
    label: String,
    enabled: Boolean,
    minDate: LocalDate?,
    maxDate: LocalDate?,
    isError: Boolean,
    errorText: String,
    valueDisplayOverride: String?,
) {
    var showDatePicker by remember { mutableStateOf(false) }

    // Convert LocalDate to display string (dd/MM/yyyy)
    val displayValue = valueDisplayOverride ?: value?.let {
        val d = it.dayOfMonth.toString().padStart(2, '0')
        val m = it.monthNumber.toString().padStart(2, '0')
        "$d/$m/${it.year}"
    } ?: ""

    // Convert LocalDate to milliseconds (start of day in local timezone)
    fun LocalDate.toEpochMilliseconds(): Long =
        atStartOfDayIn(TimeZone.currentSystemDefault()).toEpochMilliseconds()

    // Convert milliseconds to LocalDate
    fun Long.toLocalDate(): LocalDate {
        val localDateTime = Instant.fromEpochMilliseconds(this)
            .toLocalDateTime(TimeZone.currentSystemDefault())
        return LocalDate(localDateTime.year, localDateTime.monthNumber, localDateTime.dayOfMonth)
    }

    val minDateMillis = minDate?.toEpochMilliseconds()
    val maxDateMillis = maxDate?.toEpochMilliseconds()

    val initialDateMillis = remember(value) {
        value?.toEpochMilliseconds() ?: Clock.System.now().toEpochMilliseconds()
    }

    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = initialDateMillis,
        yearRange = IntRange(2020, 2100),
        selectableDates = object : SelectableDates {
            override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                val isValidMin = minDateMillis?.let { utcTimeMillis >= it } ?: true
                val isValidMax = maxDateMillis?.let { utcTimeMillis <= it } ?: true
                return isValidMin && isValidMax
            }
        }
    )

    var lastSelectedDateMillis by remember { mutableStateOf<Long?>(initialDateMillis) }

    LaunchedEffect(datePickerState.selectedDateMillis) {
        datePickerState.selectedDateMillis?.let {
            lastSelectedDateMillis = it
        }
    }

    LaunchedEffect(showDatePicker) {
        if (showDatePicker) {
            lastSelectedDateMillis = value?.toEpochMilliseconds() ?: initialDateMillis
        }
    }

    Box(modifier = modifier) {
        DefaultCircleShapedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = displayValue,
            onValueChange = {},
            label = label,
            enabled = enabled,
            readOnly = true,
            canFocus = false,
            isError = isError,
            errorText = errorText
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = p54)
                .clickable(enabled = enabled) {
                    showDatePicker = true
                }
        )
    }

    if (showDatePicker) {
        val surfaceColor = AppTheme.current.colors.surface
        MaterialTheme(
            colorScheme = MaterialTheme.colorScheme.copy(
                surface = surfaceColor,
                surfaceContainerLowest = surfaceColor,
                surfaceContainerLow = surfaceColor,
                surfaceContainer = surfaceColor,
                surfaceContainerHigh = surfaceColor,
                surfaceContainerHighest = surfaceColor
            )
        ) {
            DatePickerDialog(
                onDismissRequest = { showDatePicker = false },
                colors = DatePickerDefaults.colors(
                    containerColor = surfaceColor
                ),
                confirmButton = {
                    TextButton(
                        onClick = {
                            val selectedDateMillis = datePickerState.selectedDateMillis
                                ?: lastSelectedDateMillis
                                ?: initialDateMillis
                            val localDate = selectedDateMillis.toLocalDate()
                            onValueChange(localDate)
                            showDatePicker = false
                        }
                    ) {
                        Text(stringResource(SR.strings.ok))
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDatePicker = false }) {
                        Text(stringResource(SR.strings.cancel))
                    }
                }
            ) {
                DatePicker(state = datePickerState)
            }
        }
    }
}
